import { useEffect, useMemo, useState } from 'react';
import { Undo2, Loader2, CheckCheck } from 'lucide-react';
import { formatMoeda } from '@fiodecobre/shared';
import { api } from '../../lib/api';

// Registrar devolucao de uma VENDA CM (DA-381). SEM parte fiscal: o Venda CM saiu sem NF-e, entao
// a devolucao NAO emite nota — e' puro estorno
// de estoque + qtd_retornada. O cliente local traz de volta o nao-usado ao longo do mes; o saldo
// vem SEMPRE do backend (GET /venda-cm-saldo), nunca do estado da tela de orcamento.

interface ItemSaldo {
  orcamento_item_id: number;
  codigo: string | null;
  descricao: string;
  unidade: string;
  quantidade: string;      // saiu
  qtd_retornada: string;   // ja voltou
  saldo: string;           // quantidade - qtd_retornada
  valor_unitario: string;
}

interface SaldoResp {
  numero: string;
  cliente_nome: string | null;
  cliente_cidade: string | null;
  itens: ItemSaldo[];
}

interface Props {
  orcamentoId: number;
  onClose: () => void;
  onSucesso: () => void;
}

function parseQtd(v: string): number {
  const n = parseFloat(String(v).replace(/\./g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}
function fmtQtd(v: string | number): string {
  const n = Number(v);
  return Number.isInteger(n) ? String(n) : n.toLocaleString('pt-BR', { maximumFractionDigits: 3 });
}

export function ModalRegistrarDevolucaoVendaCm({ orcamentoId, onClose, onSucesso }: Props) {
  const [carregando, setCarregando] = useState(true);
  const [dados, setDados] = useState<SaldoResp | null>(null);
  const [voltou, setVoltou] = useState<Record<number, string>>({});
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !salvando) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, salvando]);

  useEffect(() => {
    let vivo = true;
    (async () => {
      try {
        const r = await api.get<SaldoResp>(`/api/orcamentos/${orcamentoId}/venda-cm-saldo`);
        if (vivo) setDados(r);
      } catch (e: any) {
        if (vivo) setErro(e?.message || 'Erro ao carregar o saldo da Venda CM.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [orcamentoId]);

  const itens = dados?.itens ?? [];

  const { totalItens, totalValor, temExcesso } = useMemo(() => {
    let n = 0, v = 0, excesso = false;
    for (const it of itens) {
      const q = parseQtd(voltou[it.orcamento_item_id] ?? '');
      if (q > 0) {
        n++;
        v += q * Number(it.valor_unitario);
        if (q > Number(it.saldo) + 1e-6) excesso = true;
      }
    }
    return { totalItens: n, totalValor: v, temExcesso: excesso };
  }, [itens, voltou]);

  // "Devolveu tudo": preenche cada linha com o saldo do item (o que ainda esta com o cliente).
  const temSaldo = itens.some((it) => Number(it.saldo) > 0);
  function devolveuTudo() {
    const novo: Record<number, string> = {};
    for (const it of itens) {
      if (Number(it.saldo) > 0) novo[it.orcamento_item_id] = fmtQtd(it.saldo);
    }
    setVoltou(novo);
    setErro(null);
  }

  async function confirmar() {
    if (salvando) return;
    const alvos = itens
      .map((it) => ({ orcamento_item_id: it.orcamento_item_id, qtd: parseQtd(voltou[it.orcamento_item_id] ?? '') }))
      .filter((a) => a.qtd > 0);
    if (alvos.length === 0) { setErro('Marque quanto voltou em pelo menos um item.'); return; }
    if (temExcesso) { setErro('Algum item tem "voltou agora" maior que o saldo.'); return; }
    setErro(null);
    setSalvando(true);
    try {
      await api.post(`/api/orcamentos/${orcamentoId}/venda-cm-devolver`, { itens: alvos });
      onSucesso();
    } catch (e: any) {
      setErro(e?.message || 'Erro ao registrar a devolução.');
      setSalvando(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl flex flex-col max-h-[90vh]">
        {/* Cabecalho navy */}
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-t-xl text-white" style={{ backgroundColor: '#12294f', borderBottom: '2px solid #1d4ed8' }}>
          <Undo2 size={18} />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-base">Registrar devolução</div>
            <div className="text-xs" style={{ color: '#a9bbe0' }}>
              {dados?.cliente_nome ?? '—'}{dados?.cliente_cidade ? ` · ${dados.cliente_cidade}` : ''}
            </div>
          </div>
          <button type="button" onClick={onClose} disabled={salvando} aria-label="Fechar"
            className="w-8 h-8 rounded-lg text-blue-100 hover:bg-white/10 disabled:opacity-50">✕</button>
        </div>

        <div className="p-5 overflow-y-auto">
          {/* Explicacao */}
          <div className="flex gap-2.5 items-start rounded-lg px-3 py-2.5 mb-3 text-sm" style={{ backgroundColor: '#f3f6fc', border: '1px solid #e2e7f0', color: '#47526b' }}>
            <span style={{ color: '#1d4ed8', fontWeight: 800 }}>↩</span>
            <span>Marque quanto de cada material o cliente <b>trouxe de volta agora</b>. O resto segue com ele até o fechamento do mês. Isso devolve ao estoque — <b>sem cobrar nada e sem nota</b> (a nota de venda sai só no fechamento).</span>
          </div>

          {carregando ? (
            <div className="flex items-center justify-center gap-2 py-10 text-gray-500">
              <Loader2 size={18} className="animate-spin" /> Carregando saldo…
            </div>
          ) : (
            <>
              <div className="flex items-center gap-x-4 mb-2.5 px-0.5">
                <div className="flex flex-wrap gap-x-4 gap-y-1 items-center text-xs" style={{ color: '#8791a6' }}>
                  <span><b style={{ color: '#47526b' }}>Venda CM</b> {dados?.numero}</span>
                </div>
                <div className="flex-1" />
                {temSaldo && (
                  <button type="button" onClick={devolveuTudo} disabled={salvando}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg font-bold text-[13px] text-white shrink-0 disabled:opacity-50"
                    style={{ backgroundColor: '#1d4ed8' }}>
                    <CheckCheck size={15} /> Devolveu tudo
                  </button>
                )}
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ color: '#8791a6' }} className="text-[11px] uppercase tracking-wide">
                      <th className="text-left font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0' }}>Produto</th>
                      <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0' }}>Saiu</th>
                      <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0' }}>Já voltou</th>
                      <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0' }}>Saldo</th>
                      <th className="text-right font-bold px-2.5 py-2" style={{ borderBottom: '1.5px solid #e2e7f0' }}>Voltou agora</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((it) => {
                      const saldo = Number(it.saldo);
                      const zerado = saldo <= 0;
                      const q = parseQtd(voltou[it.orcamento_item_id] ?? '');
                      const excede = q > saldo + 1e-6;
                      return (
                        <tr key={it.orcamento_item_id} style={{ borderBottom: '1px solid #eef1f6' }}>
                          <td className="px-2.5 py-2">
                            <div className="font-semibold" style={{ color: zerado ? '#8791a6' : '#0f1b33' }}>{it.descricao}</div>
                            <div className="text-[11px]" style={{ color: '#8791a6' }}>{it.codigo}</div>
                          </td>
                          <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: '#47526b' }}>{fmtQtd(it.quantidade)} <span className="text-[11px]" style={{ color: '#8791a6' }}>{it.unidade}</span></td>
                          <td className="px-2.5 py-2 text-right tabular-nums" style={{ color: '#47526b' }}>{fmtQtd(it.qtd_retornada)}</td>
                          <td className="px-2.5 py-2 text-right tabular-nums font-extrabold">
                            {zerado ? <span style={{ color: '#8791a6' }}>—</span>
                              : <span style={{ backgroundColor: '#fde68a', color: '#000', padding: '1px 8px', borderRadius: 6 }}>{fmtQtd(it.saldo)}</span>}
                          </td>
                          <td className="px-2.5 py-2 text-right">
                            {zerado ? (
                              <span className="text-[12px] font-semibold pr-1" style={{ color: '#8791a6' }}>✓ tudo</span>
                            ) : (
                              <div className="inline-flex flex-col items-end">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={voltou[it.orcamento_item_id] ?? ''}
                                  placeholder="0"
                                  onChange={(e) => setVoltou((m) => ({ ...m, [it.orcamento_item_id]: e.target.value }))}
                                  className="w-20 text-right font-bold tabular-nums rounded-lg px-2.5 py-1.5 outline-none"
                                  style={{
                                    backgroundColor: '#fffbeb',
                                    border: `1.5px solid ${excede ? '#dc2626' : '#e7d9a6'}`,
                                    color: '#0f1b33',
                                  }}
                                />
                                <span className="text-[10.5px] mt-0.5" style={{ color: excede ? '#dc2626' : '#8791a6' }}>
                                  máx. {fmtQtd(it.saldo)}
                                </span>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {erro && <div className="mt-3 text-sm rounded-lg px-3 py-2" style={{ backgroundColor: '#fef2f2', color: '#b91c1c', border: '1px solid #fecaca' }}>{erro}</div>}
        </div>

        {/* Rodape */}
        <div className="flex items-center gap-3 px-5 py-3.5 rounded-b-xl mt-auto" style={{ borderTop: '1px solid #e2e7f0', backgroundColor: '#fafbfe' }}>
          <div className="text-sm" style={{ color: '#47526b' }}>
            {totalItens > 0
              ? <>Voltando agora: <b style={{ color: '#0f1b33' }}>{totalItens} {totalItens === 1 ? 'item' : 'itens'}</b> · devolução de <b style={{ color: '#1d4ed8' }}>{formatMoeda(totalValor)}</b></>
              : <span style={{ color: '#8791a6' }}>Nenhum item marcado ainda.</span>}
          </div>
          <div className="flex-1" />
          <button type="button" onClick={onClose} disabled={salvando}
            className="px-4 py-2 rounded-lg font-semibold text-sm disabled:opacity-50"
            style={{ backgroundColor: '#fff', border: '1.5px solid #e2e7f0', color: '#47526b' }}>Cancelar</button>
          <button type="button" onClick={() => void confirmar()} disabled={salvando || totalItens === 0 || temExcesso}
            className="px-5 py-2 rounded-lg font-bold text-sm text-white flex items-center gap-2 disabled:opacity-50"
            style={{ backgroundColor: '#1d4ed8' }}>
            {salvando ? <Loader2 size={16} className="animate-spin" /> : <Undo2 size={16} />}
            Registrar devolução
          </button>
        </div>
      </div>
    </div>
  );
}
